import type { FormatTypeData, InventoryFormatTypeOption } from '@openx/types/targeting/formatType';
import {
  displayContexts,
  type TargetingFieldsValues,
  type TargetingReadOnlyProps,
} from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState';
import { CtvAlert } from '../shared/CtvValidation/CtvAlert';
import type { CtvValidationReturnType } from '../shared/CtvValidation/utils/useCtvValidation';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly';
import { FormPreviewItems } from './FormPreviewItems';
import { Items } from './Items';

type CtvValidationMethods = Pick<
  CtvValidationReturnType,
  'setIsTechnographicOpen' | 'shouldDisplayWarningInFormatType'
>;

const defaultCustomValidationMethods: CtvValidationMethods = {
  setIsTechnographicOpen: noop,
  shouldDisplayWarningInFormatType: false,
};

type FormatTypeReadOnlyProps = Omit<TargetingReadOnlyProps<FormatTypeData, CtvValidationMethods>, 'field'> & {
  field: TargetingFieldsValues;
  onRemoveParameter?: (opt: InventoryFormatTypeOption) => void;
};

export const FormatTypeReadOnly = ({
  name,
  field,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  customValidationMethods = defaultCustomValidationMethods,
  displayContext,
  onRemoveParameter,
}: FormatTypeReadOnlyProps) => {
  const { isDisabled, disabledReasonMessage } = useTargetingDisabledState(field);
  const { t } = useTranslation();
  const { shouldDisplayWarningInFormatType, setIsTechnographicOpen } = customValidationMethods;

  const invalidParams =
    displayContext === displayContexts.TRAFFIC_SET
      ? [t('Device Type = Connected TV, Set Top Box')]
      : [t('Device = Connected TV, Set Top Box')];

  const hasAdPlacement = targetingParams?.ad_placement;
  const hasDistributionChannel = targetingParams?.distribution_channel;
  const optionsCount = [hasAdPlacement, hasDistributionChannel].filter(Boolean).length;
  const hasValue = optionsCount > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        isEmpty={!targetingParams}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabled}
        description={t(
          'Define whether ads are served on the web or within mobile applications to align delivery with user environments.'
        )}
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        isSingleChoice
        disabledReasonMessage={disabledReasonMessage}
      >
        {(targetingParams || shouldDisplayWarningInFormatType) && isOpen && (
          <StyledReadOnlyTargetingDropdown>
            {targetingParams && (
              <FormPreviewItems
                targetingParams={targetingParams}
                onOpenDrawerClick={onOpenDrawerClick}
                onRemove={onRemoveClick}
                onRemoveParameter={onRemoveParameter}
                optionsCount={optionsCount}
                disabledReasonMessage={disabledReasonMessage}
              />
            )}

            {shouldDisplayWarningInFormatType && (
              <CtvAlert
                invalidField={t('Technology and Devices')}
                invalidParams={invalidParams}
                onOpen={setIsTechnographicOpen}
                dataTest={'ctv-alert-in-format-type'}
              />
            )}
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      isEmpty={!targetingParams}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabled}
      description={t(
        'Define whether ads are served on the web or within mobile applications to align delivery with user environments.'
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {targetingParams && <Items targetingParams={targetingParams} displayContext={displayContext} />}

      {shouldDisplayWarningInFormatType && (
        <CtvAlert
          invalidField={t('Technology and Devices')}
          invalidParams={invalidParams}
          onOpen={setIsTechnographicOpen}
          dataTest={'ctv-alert-in-format-type'}
        />
      )}
    </TargetingReadOnly>
  );
};
